"use client";

import React, { useState } from "react";
import { CircleAlert as AlertCircle, CircleCheck as CheckCircle2, Loader as Loader2 } from 'lucide-react';
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
const LEAD_WEBHOOK_URL = "https://hooks.zapier.com/hooks/catch/28361868/4mesgtq/";
const PROJECT_TYPE_OPTIONS = ["Kitchen", "Bathroom", "Whole-Home", "Home Addition", "Basement", "Interior", "Outdoor Living", "Deck", "Other"] as const;
const TIMING_OPTIONS = ["As soon as possible", "1-3 months", "3-6 months", "6+ months / just exploring"] as const;
const BUDGET_OPTIONS = ["Under $25,000", "$25,000-$75,000", "$75,000-$150,000", "$150,000-$300,000", "$300,000+", "Prefer not to say"] as const;
export default function EstimateRequestForm() {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [zip, setZip] = useState("");
  const projectType = "Outdoor Living";
  const [timing, setTiming] = useState("");
  const [budget, setBudget] = useState("");
  const [message, setMessage] = useState("");
  const [consent, setConsent] = useState(false);
  const [zipError, setZipError] = useState("");
  const [consentError, setConsentError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setErrorMessage("");
    let isValid = true;

    // Validate 5-digit US ZIP Code
    if (!/^\d{5}$/.test(zip.trim())) {
      setZipError("Please enter a valid 5-digit ZIP code.");
      isValid = false;
    } else {
      setZipError("");
    }

    // Validate Consent
    if (!consent) {
      setConsentError("Please agree to be contacted regarding your project.");
      isValid = false;
    } else {
      setConsentError("");
    }
    if (!isValid) return;
    setIsSubmitting(true);
    try {
      const payload = new URLSearchParams({
        name: name.trim(),
        phone: phone.trim(),
        email: email.trim(),
        project_type: projectType,
        address: zip.trim(),
        zip: zip.trim(),
        details: message.trim(),
        timing,
        budget,
        contact_method: "Phone",
        source: "Brookhaven Elevation website",
        page: typeof window !== "undefined" ? window.location.href : "",
        form_location: "outdoor-living-page"
      });
      const response = await fetch(LEAD_WEBHOOK_URL, {
        method: "POST",
        body: payload
      });
      if (response.ok) {
        setIsSuccess(true);
      } else {
        setErrorMessage("Please call (470) 847-9076 or email serviceleadalerts@gmail.com to request an estimate.");
      }
    } catch {
      setErrorMessage("Please call (470) 847-9076 or email serviceleadalerts@gmail.com to request an estimate.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="estimaterequestform" data-nav="light" className="relative bg-background text-foreground py-16 md:py-24 px-4 sm:px-6 lg:px-8">
      <div className="max-w-2xl mx-auto">
        <div className="rounded-2xl border border-border bg-card/85 backdrop-blur-md shadow-xl p-6 sm:p-10 transition-all">
          <div className="text-center mb-8">
            <span className="text-xs uppercase tracking-widest text-primary font-semibold block mb-2">
              Start your project
            </span>
            <h2 className="text-3xl sm:text-4xl font-serif text-foreground tracking-tight mb-3">
              Request an estimate
            </h2>
            <p className="text-muted-foreground text-sm sm:text-base leading-relaxed">
              Share your outdoor living ideas and we will reach out with an initial consultation plan.
            </p>
          </div>

          {isSuccess ? <div className="py-8 text-center space-y-4">
              <div className="flex justify-center">
                <CheckCircle2 className="size-12 text-primary" />
              </div>
              <h3 className="text-2xl font-serif text-foreground">
                Estimate request received
              </h3>
              <p className="text-muted-foreground max-w-md mx-auto text-sm sm:text-base">
                Thank you for reaching out. A team specialist will review your project details and contact you shortly.
              </p>
            </div> : <form onSubmit={handleSubmit} className="space-y-6" noValidate>
              {errorMessage && <Alert variant="destructive">
                  <AlertCircle className="size-4" />
                  <AlertTitle>Submission error</AlertTitle>
                  <AlertDescription>{errorMessage}</AlertDescription>
                </Alert>}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="full-name">
                    Full name <span className="text-destructive">*</span>
                  </Label>
                  <Input id="full-name" name="name" type="text" required value={name} onChange={e => setName(e.target.value)} placeholder="Your name" disabled={isSubmitting} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="phone-number">
                    Phone number <span className="text-destructive">*</span>
                  </Label>
                  <Input id="phone-number" name="phone" type="tel" required value={phone} onChange={e => setPhone(e.target.value)} placeholder="(404) 555-0123" disabled={isSubmitting} />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email-address">
                    Email address <span className="text-destructive">*</span>
                  </Label>
                  <Input id="email-address" name="email" type="email" required value={email} onChange={e => setEmail(e.target.value)} placeholder="name@example.com" disabled={isSubmitting} />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="zip-code">
                    ZIP code <span className="text-destructive">*</span>
                  </Label>
                  <Input id="zip-code" name="zip" type="text" required maxLength={5} value={zip} onChange={e => {
                setZip(e.target.value);
                if (zipError) setZipError("");
              }} placeholder="30319" aria-invalid={!!zipError} disabled={isSubmitting} />
                  {zipError && <p className="text-xs text-destructive">{zipError}</p>}
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="project-type">
                  Project type <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <select id="project-type" name="project_type" required disabled value={projectType} className="border-input flex h-9 w-full rounded-md border bg-muted/50 px-3 py-1 text-sm shadow-xs transition-colors cursor-not-allowed opacity-80 text-foreground outline-none">
                    {PROJECT_TYPE_OPTIONS.map(opt => <option key={opt} value={opt}>
                        {opt}
                      </option>)}
                  </select>
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="preferred-timing">Preferred timing</Label>
                  <select id="preferred-timing" name="timing" value={timing} onChange={e => setTiming(e.target.value)} disabled={isSubmitting} className="border-input flex h-9 w-full rounded-md border bg-background px-3 py-1 text-sm shadow-xs transition-colors text-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 outline-none">
                    <option value="">Select timeframe (optional)</option>
                    {TIMING_OPTIONS.map(opt => <option key={opt} value={opt}>
                        {opt}
                      </option>)}
                  </select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="budget-range">Budget range</Label>
                  <select id="budget-range" name="budget" value={budget} onChange={e => setBudget(e.target.value)} disabled={isSubmitting} className="border-input flex h-9 w-full rounded-md border bg-background px-3 py-1 text-sm shadow-xs transition-colors text-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 outline-none">
                    <option value="">Select budget (optional)</option>
                    {BUDGET_OPTIONS.map(opt => <option key={opt} value={opt}>
                        {opt}
                      </option>)}
                  </select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="project-details">Project details / message</Label>
                <Textarea id="project-details" name="details" rows={4} value={message} onChange={e => setMessage(e.target.value)} placeholder="Tell us about your space, dimensions, or goals..." disabled={isSubmitting} />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-start gap-3 pt-1">
                  <Checkbox id="consent" checked={consent} onCheckedChange={checked => {
                setConsent(Boolean(checked));
                if (consentError) setConsentError("");
              }} disabled={isSubmitting} aria-invalid={!!consentError} />
                  <Label htmlFor="consent" className="text-xs sm:text-sm text-muted-foreground leading-snug cursor-pointer select-none font-normal">
                    I agree to be contacted about my project by phone, text, or email.
                  </Label>
                </div>
                {consentError && <p className="text-xs text-destructive pl-7">{consentError}</p>}
              </div>

              <div className="pt-2">
                <Button type="submit" size="lg" disabled={isSubmitting} className="w-full bg-primary text-primary-foreground font-semibold py-3.5 px-7 rounded-lg hover:shadow-lg transition-all">
                  {isSubmitting ? <>
                      <Loader2 className="size-4 animate-spin mr-2" />
                      Submitting request...
                    </> : "Submit estimate request"}
                </Button>
              </div>
            </form>}
        </div>
      </div>
    </section>;
}